import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Trophy, Play, Crown, ScrollText, Trash2, Sparkles, Shield, ArrowRight, Plus, Edit, Save, X, Maximize2, Link as LinkIcon, Settings, ListOrdered } from "lucide-react";
import { toast } from "sonner";

// دالة مساعدة لتسمية الجولات بناءً على موقعها في السلم
function getRoundTitle(roundIndex, totalRounds) {
  const reverseIndex = totalRounds - 1 - roundIndex;
  if (reverseIndex === 0) return "النهائي (Finals)";
  if (reverseIndex === 1) return "نصف النهائي (Semi-Finals)";
  if (reverseIndex === 2) return "ربع النهائي (Quarter-Finals)";
  return `الجولة ${roundIndex + 1}`;
}

function Bracket({ bracket, clans, champion }) {
  if (!bracket || bracket.length === 0) {
    return <div className="text-white/40 text-center py-12 text-xs">سيظهر سلم البطولة فور انطلاق المباريات وتوزيع الفرق</div>;
  }
  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-6 min-w-max">
        {bracket.map((round, ri) => (
          <div key={`r-${ri}`} className="flex flex-col gap-4 justify-around min-w-[240px]">
            <div className="text-xs uppercase font-bold tracking-widest text-gold-400 text-center mb-1 bg-gold-500/10 border border-gold-500/20 py-1.5 rounded-xl">
              {getRoundTitle(ri, bracket.length)}
            </div>
            {round.map((slot, si) => {
              const a = slot.clan_a_id ? clans?.[slot.clan_a_id] : null;
              const b = slot.clan_b_id ? clans?.[slot.clan_b_id] : null;
              const winA = !!slot.winner_id && slot.winner_id === slot.clan_a_id;
              const winB = !!slot.winner_id && slot.winner_id === slot.clan_b_id;
              const formatLabel = (slot.match_format || "bo3").toUpperCase();

              return (
                <div key={slot.match_id || `slot-${ri}-${si}`} className="bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden shadow-xl" data-testid={`bracket-${ri}-${si}`}>
                  <div className="px-3.5 py-2 bg-black/40 text-[10px] text-white/40 flex justify-between items-center border-b border-white/5">
                    <span>مباراة {si + 1}</span>
                    <span className="text-gold-400 font-mono font-bold px-2 py-0.5 rounded bg-gold-500/10 border border-gold-500/20">{formatLabel}</span>
                  </div>
                  <SlotLine clan={a} win={winA} bye={!slot.clan_a_id && !!slot.resolved} score={slot.score_a} />
                  <div className="border-t border-white/5" />
                  <SlotLine clan={b} win={winB} bye={!slot.clan_b_id && !!slot.resolved} score={slot.score_b} />
                  {slot.match_id && (
                    <Link to={`/matches/${slot.match_id}`} className="block bg-gold-500/10 text-center py-2 text-[10px] uppercase font-black tracking-widest text-gold-400 hover:bg-gold-500/20 transition">
                      عرض تفاصيل المباراة <ArrowRight size={10} className="inline mr-1" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {champion && clans?.[champion] && (
        <div className="mt-8 mx-auto max-w-md bg-gradient-to-b from-gold-500/15 via-[#111113]/90 to-[#111113]/90 border border-gold-500/40 rounded-3xl p-6 text-center shadow-2xl">
          <Crown className="mx-auto text-gold-400 drop-shadow-[0_0_12px_rgba(255,204,0,0.6)]" size={40} />
          <div className="text-[10px] uppercase font-black tracking-widest text-gold-400 mt-2">الكلان المتوج بلقب البطولة 🏆</div>
          <div className="font-display font-black text-3xl text-white mt-1">{clans[champion].name}</div>
        </div>
      )}
    </div>
  );
}

function SlotLine({ clan, win, bye, score }) {
  if (bye) {
    return <div className="px-4 py-3 text-xs text-white/30 italic">— BYE (تأهل تلقائي) —</div>;
  }
  if (!clan) {
    return <div className="px-4 py-3 text-xs text-white/30">في انتظار الخصم...</div>;
  }
  return (
    <div className={`px-4 py-3 text-sm flex items-center justify-between gap-2 transition ${win ? "bg-gold-500/15 text-gold-400 font-black" : "text-white/80"}`}>
      <div className="flex items-center gap-2.5 min-w-0">
        <Shield size={14} className={win ? "text-gold-400" : "text-white/30"} />
        <span className="truncate font-bold">{clan.name}</span>
      </div>
      <span className="text-[11px] font-mono shrink-0">{Number.isInteger(score) ? score : `[${clan.tag}]`}</span>
    </div>
  );
}

// 🏆 نافذة إعدادات وأنظمة الأدوار للبطولة عند الإنشاء
function CreateTournamentModal({ isOpen, onClose, onSuccess }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [maxParticipants, setMaxParticipants] = useState(16);
  
  const [roundFormats, setRoundFormats] = useState({
    default: "bo3",
    quarter_finals: "bo3",
    semi_finals: "bo5",
    finals: "bo7",
  });

  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name,
      description,
      max_participants: Number(maxParticipants),
      round_formats: roundFormats,
    };

    try {
      await api.post("/tournaments", payload);
      toast.success("تم إنشاء البطولة بنجاح! 🎉");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "حدث خطأ أثناء إنشاء البطولة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200" dir="rtl">
      <div className="bg-[#141417] border border-white/15 rounded-3xl max-w-lg w-full p-6 md:p-8 space-y-5 text-white shadow-2xl relative">
        <button onClick={onClose} className="absolute top-5 left-5 text-white/40 hover:text-white transition p-1.5 rounded-lg hover:bg-white/5">
          <X size={18} />
        </button>

        <div className="flex items-center gap-3 border-b border-white/10 pb-4">
          <div className="h-10 w-10 rounded-xl bg-gold-500/10 border border-gold-500/30 text-gold-400 grid place-items-center">
            <Trophy size={20} />
          </div>
          <div>
            <h2 className="font-display font-black text-xl text-white">إنشاء بطولة جديدة 🏆</h2>
            <p className="text-xs text-white/50">تحديد شروط وسلم المواجهات وخروج المغلوب</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-white/70 mb-1.5">اسم البطولة</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="مثال: بطولة رايفلز الكبرى"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/50 text-sm text-white"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-white/70 mb-1.5">وصف البطولة</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="تفاصيل ورعاة البطولة..."
              rows={2}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/50 text-sm text-white resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-white/70 mb-1.5">الحد الأقصى للكلانات</label>
            <select
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 outline-none focus:border-gold-500/50 text-sm text-white"
            >
              <option value={8}>8 كلانات</option>
              <option value={16}>16 كلان</option>
              <option value={12}>12 كلان</option>
              <option value={4}>4 كلانات</option>
              <option value={2}>2 كلان</option>
            </select>
          </div>

          {/* ⚙️ قسم اختيار أنظمة الأدوار المتنوعة */}
          <div className="bg-black/40 border border-white/10 rounded-2xl p-4 space-y-3 shadow-inner">
            <div className="text-xs font-bold text-gold-400 flex items-center gap-1.5">
              <Settings size={14} /> نظام مواجهات الأدوار (Best Of)
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block text-white/60 mb-1 font-bold">الأدوار التمهيدية</label>
                <select
                  value={roundFormats.default}
                  onChange={(e) => setRoundFormats({ ...roundFormats, default: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 outline-none text-white focus:border-gold-500/50 font-mono"
                >
                  <option value="bo1">BO1 (شوط واحد)</option>
                  <option value="bo3">BO3 (الأفضل من 3)</option>
                  <option value="bo5">BO5 (الأفضل من 5)</option>
                </select>
              </div>

              <div>
                <label className="block text-white/60 mb-1 font-bold">ربع النهائي</label>
                <select
                  value={roundFormats.quarter_finals}
                  onChange={(e) => setRoundFormats({ ...roundFormats, quarter_finals: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 outline-none text-white focus:border-gold-500/50 font-mono"
                >
                  <option value="bo3">BO3 (الأفضل من 3)</option>
                  <option value="bo5">BO5 (الأفضل من 5)</option>
                  <option value="bo7">BO7 (الأفضل من 7)</option>
                </select>
              </div>

              <div>
                <label className="block text-white/60 mb-1 font-bold">نصف النهائي</label>
                <select
                  value={roundFormats.semi_finals}
                  onChange={(e) => setRoundFormats({ ...roundFormats, semi_finals: e.target.value })}
                  className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2 outline-none text-white focus:border-gold-500/50 font-mono"
                >
                  <option value="bo3">BO3 (الأفضل من 3)</option>
                  <option value="bo5">BO5 (الأفضل من 5)</option>
                  <option value="bo7">BO7 (الأفضل من 7)</option>
                </select>
              </div>

              <div>
                <label className="block text-gold-400 font-bold mb-1">المباراة النهائية</label>
                <select
                  value={roundFormats.finals}
                  onChange={(e) => setRoundFormats({ ...roundFormats, finals: e.target.value })}
                  className="w-full bg-black/50 border border-gold-500/40 rounded-xl px-3 py-2 outline-none text-gold-400 font-bold font-mono focus:border-gold-500"
                >
                  <option value="bo3">BO3 (الأفضل من 3)</option>
                  <option value="bo5">BO5 (الأفضل من 5)</option>
                  <option value="bo7">BO7 (الأفضل من 7)</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-xs text-white/70 font-bold hover:bg-white/5 transition"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black text-xs hover:bg-gold-400 inline-flex items-center gap-1.5 shadow-md transition disabled:opacity-50"
            >
              <Save size={15} /> {loading ? "جاري الإنشاء..." : "إنشاء البطولة"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TournamentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [refreshError, setRefreshError] = useState(false);
  const [starting, setStarting] = useState(false);
  const requestRef = useRef(null);
  const [clanMembersCount, setClanMembersCount] = useState(0);

  const [activeTab, setActiveTab] = useState("standings");
  const [tournamentRules, setTournamentRules] = useState([]);
  const [creatingRule, setCreatingRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", order: 1, images: [] });
  const [imageUrlInput, setImageUrlInput] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const { data } = await api.get(`/tournaments/${id}`, { signal: controller.signal, timeout: 12000 });
      if (controller.signal.aborted) return;
      setRefreshError(false);
      setT(data);
      if (data.rules_list && Array.isArray(data.rules_list) && data.rules_list.length > 0) {
        setTournamentRules(data.rules_list);
      } else if (data.rules) {
        setTournamentRules([{ id: "rule-legacy", title: "القانون الرئيسي", body: data.rules, order: 1, images: [] }]);
      } else {
        setTournamentRules([]);
      }
    } catch {
      if (!controller.signal.aborted) setRefreshError(true);
    }
  }, [id]);

  useEffect(() => {
    let stopped = false;
    let timer;
    let polling = false;
    setT(null);
    const poll = async () => {
      if (stopped || polling) return;
      clearTimeout(timer);
      polling = true;
      if (!document.hidden) await load();
      polling = false;
      if (!stopped) timer = setTimeout(poll, 3000);
    };
    const onVisible = () => { if (!document.hidden) poll(); };
    poll();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      requestRef.current?.abort();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  useEffect(() => {
    if (!user?.clan_id) {
      setClanMembersCount(0);
      return;
    }
    api.get(`/clans/${user.clan_id}`)
      .then((r) => {
        const c = r.data || {};
        const count = Array.isArray(c.member_ids)
          ? c.member_ids.length
          : (Array.isArray(c.members) ? c.members.length : 0);
        setClanMembersCount(count);
      })
      .catch(() => setClanMembersCount(0));
  }, [user?.clan_id]);

  const isStaff = user && (user.role === "admin" || user.role === "owner");

  const myClanRegistered = useMemo(
    () => !!(user?.clan_id && t?.participants?.includes(user.clan_id)),
    [user?.clan_id, t?.participants]
  );

  const plusOnlyNow = useMemo(() => {
    if (!t) return false;
    if (t.status !== "registration") return false;
    try {
      return new Date() < new Date(t.plus_window_until);
    } catch { return false; }
  }, [t]);

  const handleErr = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail));

  const join = async () => {
    if (clanMembersCount < 6) {
      return toast.error("لا يمكنك المشاركة في البطولة إلا بعد وصول عدد أعضاء الكلان إلى 6 لاعبين على الأقل.");
    }
    try { await api.post(`/tournaments/${id}/join`); toast.success("تم تسجيل كلانك بنجاح! 🏆"); load(); }
    catch (err) { handleErr(err); }
  };

  const start = async () => {
    if (starting || !window.confirm("إجراء القرعة العشوائية وبدء البطولة؟ سيُغلق التسجيل وتُحفظ القرعة دون إعادة سحب.")) return;
    setStarting(true);
    try { await api.post(`/tournaments/${id}/start`); toast.success("تمت القرعة وبدأت البطولة! 🚀"); await load(); }
    catch (err) { handleErr(err); }
    finally { setStarting(false); }
  };

  const remove = async () => {
    if (!window.confirm("حذف البطولة نهائياً؟")) return;
    try { await api.delete(`/tournaments/${id}`); window.location.href = "/tournaments"; }
    catch (err) { handleErr(err); }
  };

  const startCreateRule = () => {
    setForm({ title: "", body: "", order: tournamentRules.length + 1, images: [] });
    setImageUrlInput("");
    setCreatingRule(true);
    setEditingRule(null);
  };

  const startEditRule = (rule) => {
    setForm({ title: rule.title, body: rule.body, order: rule.order || 1, images: rule.images || [] });
    setImageUrlInput("");
    setEditingRule(rule.id);
    setCreatingRule(false);
  };

  const cancelRuleForm = () => {
    setCreatingRule(false);
    setEditingRule(null);
    setImageUrlInput("");
    setForm({ title: "", body: "", order: 1, images: [] });
  };

  const addImageUrl = () => {
    if (!imageUrlInput.trim()) return;
    setForm(p => ({ ...p, images: [...p.images, imageUrlInput.trim()] }));
    setImageUrlInput("");
  };

  const saveRule = async (e) => {
    e.preventDefault();
    setSavingRule(true);

    let updatedList = [];
    if (editingRule) {
      updatedList = tournamentRules.map((r) =>
        r.id === editingRule ? { ...r, title: form.title, body: form.body, order: form.order, images: form.images } : r
      );
    } else {
      const newRule = {
        id: `rule-${Date.now()}`,
        title: form.title,
        body: form.body,
        order: form.order,
        images: form.images
      };
      updatedList = [...tournamentRules, newRule];
    }

    try {
      const combinedText = updatedList.map((r) => `${r.title}: ${r.body}`).join("\n");
      await api.put(`/tournaments/${id}`, { rules: combinedText, rules_list: updatedList });
      toast.success("تم حفظ القاعدة بنجاح");
      cancelRuleForm();
      load();
    } catch (err) {
      handleErr(err);
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("حذف هذه القاعدة؟")) return;
    const updatedList = tournamentRules.filter((r) => r.id !== ruleId);
    try {
      const combinedText = updatedList.map((r) => `${r.title}: ${r.body}`).join("\n");
      await api.put(`/tournaments/${id}`, { rules: combinedText, rules_list: updatedList });
      toast.success("تم حذف القاعدة");
      load();
    } catch (err) {
      handleErr(err);
    }
  };

  const removeImageAt = (index) => {
    setForm(p => ({ ...p, images: p.images.filter((_, i) => i !== index) }));
  };

  if (!t) return <div className="text-white/40 text-center py-12 text-xs">{refreshError ? "تعذر تحميل البطولة. نحاول الاتصال مجددًا..." : "جارٍ تحميل تفاصيل البطولة..."}</div>;

  const meetsMinMembers = clanMembersCount >= 6;
  const canJoin = t.status === "registration" && user?.clan_id && !myClanRegistered && meetsMinMembers &&
    t.participants.length < t.max_participants;

  const plusUntil = new Date(t.plus_window_until);
  const hoursLeft = Math.max(0, Math.ceil((plusUntil - new Date()) / 3600000));

  return (
    <div className="space-y-6" dir="rtl">
      
      {/* ── Header ── */}
      <div className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl pointer-events-none" />
        
        <div className="relative flex items-start gap-5 flex-wrap">
          <div className="h-16 w-16 rounded-2xl bg-gold-500/10 border border-gold-500/30 text-gold-400 grid place-items-center shrink-0 shadow-md">
            <Trophy size={30} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-400 text-[10px] font-black uppercase tracking-widest mb-1.5">
              بطولة رسمية • COD
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl text-white">{t.name}</h1>
            {t.description && <p className="text-white/60 text-xs md:text-sm mt-1.5 leading-relaxed">{t.description}</p>}
            
            <div className="mt-3.5 flex flex-wrap gap-3 text-xs text-white/50 font-bold">
              <span className="bg-white/5 px-2.5 py-1 rounded-xl border border-white/10">{t.participants?.length || 0} / {t.max_participants} كلان</span>
              <span>•</span>
              <span className="bg-white/5 px-2.5 py-1 rounded-xl border border-white/10">{t.status === "registration" ? "التسجيل مفتوح 🟢" : t.status === "live" ? "البطولة جارية 🔥" : "منتهية 🏆"}</span>
              {plusOnlyNow && (
                <>
                  <span>•</span>
                  <span className="text-gold-400 inline-flex items-center gap-1 bg-gold-500/10 border border-gold-500/20 px-2.5 py-1 rounded-xl">
                    <Sparkles size={12} /> Plus فقط ({hoursLeft} ساعة متبقية)
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-2.5 flex-wrap">
            {canJoin && (
              <button data-testid="join-tournament" onClick={join} className="px-5 py-2.5 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 text-xs shadow-md transition">
                سجّل كلانك في البطولة
              </button>
            )}
            {t.status === "registration" && user?.clan_id && !myClanRegistered && !meetsMinMembers && (
              <div className="px-3.5 py-2 rounded-xl bg-destructive/10 text-destructive text-xs border border-destructive/30 font-bold">
                ⚠️ يلزم 6 لاعبين بالكلان للمشاركة.
              </div>
            )}
            {myClanRegistered && (
              <div className="px-4 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-black">
                ✓ كلانك مسجّل في البطولة
              </div>
            )}
            {isStaff && t.status === "registration" && t.participants.length >= 2 && (
              <button data-testid="start-tournament" disabled={starting || (t.participants?.length || 0) < 2} onClick={start} className="px-5 py-2.5 rounded-xl bg-destructive text-white font-black hover:bg-destructive/90 flex items-center gap-1.5 text-xs shadow-md transition">
                <Play size={15} /> {starting ? "جاري إجراء القرعة..." : "إجراء القرعة وبدء البطولة"}
              </button>
            )}
            {isStaff && (
              <button data-testid="delete-tournament" onClick={remove} className="px-3 py-2.5 rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 transition" title="حذف البطولة">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs Selector ── */}
      <div className="flex justify-start">
        <div className="inline-flex items-center bg-[#111113]/90 border border-white/10 rounded-2xl p-1.5 shadow-xl">
          <button
            onClick={() => setActiveTab('standings')}
            className={`px-6 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
              activeTab === 'standings' ? 'bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]' : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <Shield size={16} /> سلم المواجهات (Bracket)
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-6 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2 transition-all duration-300 ${
              activeTab === 'rules' ? 'bg-gold-500 text-black font-black shadow-[0_0_15px_rgba(255,204,0,0.3)]' : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <ScrollText size={16} /> قوانين وشروط البطولة
          </button>
        </div>
      </div>

      {/* ── Tab 1: Standings / Bracket ── */}
      {activeTab === "standings" && (
        <div className="space-y-6">
          <section className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl">
            <h2 className="font-display font-black text-xl text-white mb-5 flex items-center gap-2">
              <ListOrdered size={20} className="text-gold-500" /> سلم خروج المغلوب (Tournament Bracket)
            </h2>
            <p className={`text-xs mb-3 ${refreshError ? "text-amber-400" : "text-white/50"}`} role="status">
              {refreshError ? "تعذر تحديث النتائج؛ نحاول الاتصال مجددًا. النتائج المعروضة قديمة." : "شجرة عامة • تتحدث النتائج تلقائيًا كل 3 ثوانٍ أثناء فتح الصفحة"}
            </p>
            <Bracket bracket={t.bracket} clans={t.clans} champion={t.champion_clan_id} />
          </section>

          {t.status === "registration" && (
            <section className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 shadow-2xl">
              <h2 className="font-display font-black text-lg text-white mb-4">الكلانات المسجلة ({t.participants?.length || 0})</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(t.participants || []).map((cid) => {
                  const c = t.clans?.[cid];
                  return (
                    <Link key={cid} to={`/clans/${cid}`} className="bg-black/40 border border-white/10 rounded-2xl p-3.5 flex items-center gap-3 hover:border-gold-500/40 transition shadow-sm">
                      <div className="h-9 w-9 rounded-xl bg-gold-500/10 border border-gold-500/20 text-gold-500 grid place-items-center shrink-0">
                        <Shield size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-xs text-white truncate">{c?.name || "—"}</div>
                        <div className="text-[10px] text-white/40 font-mono">[{c?.tag}]</div>
                      </div>
                    </Link>
                  );
                })}
                {(t.participants || []).length === 0 && (
                  <div className="col-span-full text-center text-white/40 py-8 text-xs">لا توجد كلانات مسجلة حتى الآن</div>
                )}
              </div>
            </section>
          )}
        </div>
      )}

      {/* ── Tab 2: Rules ── */}
      {activeTab === "rules" && (
        <section className="bg-[#111113]/90 border border-white/10 rounded-3xl overflow-hidden shadow-2xl" data-testid="tournament-detail-rules">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between gap-3">
            <h2 className="font-display font-black text-lg text-white">قوانين وشروط البطولة</h2>
            {isStaff && (
              <button
                onClick={startCreateRule}
                className="px-3.5 py-2 rounded-xl bg-gold-500 text-black text-xs font-black hover:bg-gold-400 inline-flex items-center gap-1.5 shadow-md transition"
                data-testid="tournament-add-rule-btn"
              >
                <Plus size={15} /> إضافة قاعدة جديدة
              </button>
            )}
          </div>

          <div className="p-6 space-y-5">
            {(creatingRule || editingRule) && isStaff && (
              <form onSubmit={saveRule} className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-3.5 shadow-xl" data-testid="tournament-rule-form">
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="عنوان القاعدة"
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-gold-500/50 text-white text-sm font-bold"
                />
                <textarea
                  value={form.body}
                  onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                  placeholder="وصف وتفاصيل القاعدة..."
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-gold-500/50 resize-none text-white text-sm"
                />
                <div className="flex items-center gap-3">
                  <span className="text-xs text-white/50 font-bold">الترتيب:</span>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm((p) => ({ ...p, order: Number(e.target.value) }))}
                    className="w-24 bg-black/40 border border-white/10 rounded-xl px-3 py-1.5 outline-none text-white text-sm font-mono text-center"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    placeholder="ألصق رابط صورة مباشر (https://.../image.png)"
                    className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3.5 py-2 outline-none focus:border-gold-500/50 text-white text-xs"
                  />
                  <button
                    type="button"
                    onClick={addImageUrl}
                    className="px-3.5 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-xs text-white font-bold inline-flex items-center gap-1 transition"
                  >
                    <LinkIcon size={13} /> إضافة الرابط
                  </button>
                </div>

                {form.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2">
                    {form.images.map((img, idx) => (
                      <div key={`form-rule-img-${idx}`} className="relative rounded-xl border border-white/10 overflow-hidden bg-black/40">
                        <img src={img} alt={`rule-${idx}`} className="h-24 w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImageAt(idx)}
                          className="absolute top-1 left-1 h-6 w-6 rounded-lg bg-black/80 text-white grid place-items-center hover:bg-destructive transition"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 justify-end pt-2">
                  <button type="submit" disabled={savingRule} className="px-5 py-2 rounded-xl bg-gold-500 text-black font-black hover:bg-gold-400 inline-flex items-center gap-1 disabled:opacity-60 text-xs shadow-md transition">
                    <Save size={14} /> {savingRule ? "جاري الحفظ..." : "حفظ القاعدة"}
                  </button>
                  <button type="button" onClick={cancelRuleForm} className="px-4 py-2 rounded-xl hover:bg-white/5 inline-flex items-center gap-1 text-white/70 text-xs font-bold transition">
                    <X size={14} /> إلغاء
                  </button>
                </div>
              </form>
            )}

            {tournamentRules.length > 0 ? (
              <div className="space-y-4" data-testid="tournament-rules-cards">
                {tournamentRules.map((rule, idx) => (
                  <article key={rule.id || idx} className="relative rounded-2xl border border-white/10 bg-black/40 p-5 overflow-hidden shadow-md">
                    <div className="absolute inset-y-0 right-0 w-1 bg-gold-500" />
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gold-400 font-display font-black text-sm">#{idx + 1}</span>
                          <h3 className="font-display font-black text-base text-white truncate">{rule.title || "قاعدة"}</h3>
                        </div>
                        <p className="text-xs text-white/70 mt-2 leading-relaxed whitespace-pre-wrap">{rule.body || "—"}</p>
                        
                        {Array.isArray(rule.images) && rule.images.length > 0 && (
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {rule.images.map((img, imageIdx) => (
                              <div
                                key={`${rule.id}-img-${imageIdx}`}
                                className="relative group cursor-pointer overflow-hidden rounded-xl border border-white/10 bg-black/30"
                                onClick={() => setSelectedImage(img)}
                              >
                                <img
                                  src={img}
                                  alt={`${rule.title}-${imageIdx + 1}`}
                                  className="w-full h-36 object-cover transition-transform duration-300 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Maximize2 className="text-white" size={20} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {isStaff && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => startEditRule(rule)} className="p-2 rounded-xl hover:bg-white/10 text-white/60 hover:text-white transition" title="تعديل">
                            <Edit size={15} />
                          </button>
                          <button onClick={() => deleteRule(rule.id)} className="p-2 rounded-xl hover:bg-destructive/10 text-destructive transition" title="حذف">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-white/40 text-xs py-8 text-center bg-black/20 rounded-2xl border border-white/5">لا توجد قواعد مضافة لهذه البطولة حالياً.</div>
            )}
          </div>
        </section>
      )}

      {/* ── Lightbox Modal for Images ── */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white bg-black/50 rounded-full transition-colors"
              title="إغلاق"
            >
              <X size={24} />
            </button>
            <img
              src={selectedImage}
              alt="صورة مكبرة"
              className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl border border-white/10"
            />
          </div>
        </div>
      )}

      <CreateTournamentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={load}
      />
    </div>
  );
}
